import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import TimelapseOutlinedIcon from "@mui/icons-material/TimelapseOutlined";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CloseIcon from "@mui/icons-material/Close";
import apiClient from "../../../apiClient/index";
import {
  FREEZER_FROST_DASHBOARD_STATS,
  FREEZER_FROST_NON_COMPLIANCE_LIST,
  FREEZER_FROST_CUMULATIVE_LIMIT_LIST,
  FREEZER_FROST_EXCEEDED_EXPOSURE_LIST,
  FREEZER_FROST_ACTIVE_INSIDE_LIST,
  FREEZER_FROST_DASHBOARD_SESSIONS_EXPORT,
} from "../../../apiClient/endpoints";
import FreezerFrostFilters from "./FreezerFrostFilters";
import FreezerFrostShareModal from "./FreezerFrostShareModal";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import axios from "axios";

const DASHBOARD_REFRESH_MS = 15000;
const RECENT_SESSIONS_PAGE_SIZE = 25;

const sessionStatusMeta = (sessionStatus) => {
  switch (sessionStatus) {
    case "in_progress":
    case "entry_open":
      return {
        key: "in_progress",
        label: "In Progress",
        title: "AA currently inside freezer",
        dot: "green-blink",
      };
    case "cumulative_limit_reached":
      return {
        key: "cumulative_limit_reached",
        label: "Cumulative Limit Reached",
        title: "Rolling window total reached the cumulative exposure limit",
        dot: "red-blink",
      };
    case "completed_cumulative":
      return {
        key: "completed_cumulative",
        label: "Completed",
        title: "Cumulative time exceeded",
        dot: "red-solid",
      };
    case "exceeded_no_exit":
    case "exit_missed":
    case "auto_closed_missing_exit":
      return {
        key: "exceeded_no_exit",
        label: "Exceeded Exposure Limit",
        title: "Entry scanned — no exit scan",
        dot: "red-blink",
      };
    case "exceeded_with_exit":
      return {
        key: "exceeded_with_exit",
        label: "Exceeded Exposure Limit",
        title: "Entry and exit scanned — time limit exceeded",
        dot: "red-solid-pulse",
      };
    case "non_compliance":
    case "closed_non_compliance":
      return {
        key: "non_compliance",
        label: "Non-Compliance",
        title: "Closed on entry non-compliance",
        dot: "amber-solid",
      };
    case "completed":
    default:
      return {
        key: "completed",
        label: "Completed",
        title: "Session completed within limit",
        dot: "green-solid",
      };
  }
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const mins = Math.round(seconds / 60);
  return `${mins} min`;
};

/** Exit Time only after Exit QR + exit questionnaire (completed). */
const displayExitTime = (row) => {
  if (
    row?.exitProcessComplete ||
    row?.sessionStatus === "completed" ||
    row?.sessionStatus === "exceeded_with_exit" ||
    row?.sessionStatus === "completed_cumulative"
  ) {
    return formatWhen(row.exitTime);
  }
  if (row?.rawStatus === "completed" || row?.status === "completed") return formatWhen(row.exitTime);
  return "—";
};

const isLiveInsideSession = (row) =>
  row?.isOpenSession ||
  row?.rawStatus === "entry_open" ||
  row?.sessionStatus === "in_progress" ||
  row?.sessionStatus === "entry_open";

const formatLiveElapsed = (entryTime, nowMs) => {
  if (!entryTime) return "—";
  const start = new Date(entryTime).getTime();
  if (Number.isNaN(start)) return "—";
  const totalSec = Math.max(0, Math.floor((nowMs - start) / 1000));
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins < 60) return `${mins}m ${String(secs).padStart(2, "0")}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m ${String(secs).padStart(2, "0")}s`;
};

const getRemainingExposure = (entryTime, exposureLimitSeconds, nowMs) => {
  const limitSec = Number(exposureLimitSeconds);
  if (!entryTime || !Number.isFinite(limitSec) || limitSec <= 0) {
    return { remainingSec: null, reached: false };
  }
  const start = new Date(entryTime).getTime();
  if (Number.isNaN(start)) return { remainingSec: null, reached: false };
  const elapsed = Math.max(0, Math.floor((nowMs - start) / 1000));
  const remainingSec = limitSec - elapsed;
  return { remainingSec, reached: remainingSec <= 0 };
};

const formatRemainingExposure = (entryTime, exposureLimitSeconds, nowMs) => {
  const { remainingSec, reached } = getRemainingExposure(entryTime, exposureLimitSeconds, nowMs);
  if (remainingSec == null) return { text: "—", tone: "muted", reached: false };
  if (reached) return { text: "Exposure limit reached.", tone: "danger", reached: true };
  const mins = Math.floor(remainingSec / 60);
  const secs = remainingSec % 60;
  if (mins < 60) {
    return {
      text: `${mins}m ${String(secs).padStart(2, "0")}s remaining`,
      tone: mins < 5 ? "warn" : "ok",
      reached: false,
    };
  }
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return {
    text: `${hours}h ${remMins}m remaining`,
    tone: "ok",
    reached: false,
  };
};

const SessionStatusIndicator = ({ sessionStatus, isOpenSession = false }) => {
  const meta = sessionStatusMeta(sessionStatus);
  const isLive = isOpenSession || meta.key === "in_progress";
  return (
    <div
      className={`ff-dashboard-v2__sess-status ff-dashboard-v2__sess-status--${meta.key}${isLive ? " is-live-inside" : ""}`}
      title={meta.title}
    >
      <span className={`ff-dashboard-v2__sess-dot ff-dashboard-v2__sess-dot--${meta.dot}`} aria-hidden>
        <span className="ff-dashboard-v2__sess-dot-core" />
        {meta.dot === "green-blink" ||
        meta.dot === "red-blink" ||
        meta.dot === "red-solid-pulse" ||
        meta.dot === "violet-solid-pulse" ? (
          <>
            <span className="ff-dashboard-v2__sess-dot-ping" />
            <span className="ff-dashboard-v2__sess-dot-ping ff-dashboard-v2__sess-dot-ping--delay" />
          </>
        ) : null}
      </span>
      <div className="ff-dashboard-v2__sess-status-text">
        <strong>
          {meta.label}
          {isLive ? <span className="ff-dashboard-v2__sess-live-tag">LIVE</span> : null}
        </strong>
        <small>{meta.title}</small>
      </div>
    </div>
  );
};

const FreezerFrostDashboard = () => {
  const { groupId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const userData = useSelector((state) => state.userSlice);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dates, setDates] = useState({ start: null, end: null });
  const [week, setWeek] = useState("");
  const [site, setSite] = useState("");
  const [aaAlias, setAaAlias] = useState("");
  const [filterOptions, setFilterOptions] = useState({ sites: [], aaAliases: [], weeks: [] });
  const [showNcModal, setShowNcModal] = useState(false);
  const [ncLoading, setNcLoading] = useState(false);
  const [ncList, setNcList] = useState([]);
  const [expandedNc, setExpandedNc] = useState(null);
  const [showCumulativeModal, setShowCumulativeModal] = useState(false);
  const [cumulativeLoading, setCumulativeLoading] = useState(false);
  const [cumulativeList, setCumulativeList] = useState([]);
  const [showExceededModal, setShowExceededModal] = useState(false);
  const [exceededLoading, setExceededLoading] = useState(false);
  const [exceededList, setExceededList] = useState([]);
  const [showActiveModal, setShowActiveModal] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeList, setActiveList] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [groupName, setGroupName] = useState(userData?.user?.group?.name || "");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [sessionsPage, setSessionsPage] = useState(1);
  const exportRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (week) {
      params.week = week;
      params.year = filterOptions.year || new Date().getFullYear();
    } else if (dates.start && dates.end) {
      params.start = dates.start.toISOString();
      params.end = dates.end.toISOString();
    } else if (dates.start) {
      params.start = dates.start.toISOString();
      params.end = (dates.end || dates.start).toISOString();
    }
    if (site) params.site = site;
    if (aaAlias) params.aaAlias = aaAlias;
    params.page = sessionsPage;
    params.pageSize = RECENT_SESSIONS_PAGE_SIZE;
    return params;
  }, [week, dates.start, dates.end, site, aaAlias, filterOptions.year, sessionsPage]);

  // Filters change → back to page 1 (do not touch page on silent refresh).
  useEffect(() => {
    setSessionsPage(1);
  }, [week, dates.start, dates.end, site, aaAlias]);

  const loadStats = useCallback(
    async ({ silent = false } = {}) => {
      if (!groupId || !configData?.config?.API_URL) return;
      if (!silent) setLoading(true);
      try {
        const data = await apiClient.get(
          `${configData.config.API_URL}${FREEZER_FROST_DASHBOARD_STATS}/${groupId}`,
          { params: buildFilterParams() }
        );
        setStats(data || {});
        if (data?.filterOptions) setFilterOptions(data.filterOptions);
        if (data?.recentSessionsPage && data.recentSessionsPage !== sessionsPage) {
          setSessionsPage(data.recentSessionsPage);
        }
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (!silent) setStats({});
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [groupId, configData?.config?.API_URL, buildFilterParams]
  );

  // Initial + whenever filters change
  useEffect(() => {
    loadStats({ silent: false });
  }, [loadStats]);

  // Auto-refresh KPIs + Recent Sessions (pause when tab hidden)
  useEffect(() => {
    if (!groupId || !configData?.config?.API_URL) return undefined;

    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      loadStats({ silent: true });
    };

    const id = setInterval(tick, DASHBOARD_REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [groupId, configData?.config?.API_URL, loadStats]);

  useEffect(() => {
    let cancelled = false;
    const loadGroup = async () => {
      if (!groupId || !configData?.config?.UNO_URL || !userData?.token) return;
      try {
        const { data } = await axios.get(`${configData.config.UNO_URL}/api/user/group/details/${groupId}`, {
          headers: { Authorization: `Bearer ${userData.token}` },
        });
        const name = data?.data?.name || data?.data?.data?.name;
        if (!cancelled && name) setGroupName(name);
      } catch (error) {
        // keep fallback from user.group.name
      }
    };
    loadGroup();
    return () => {
      cancelled = true;
    };
  }, [groupId, configData?.config?.UNO_URL, userData?.token]);

  useEffect(() => {
    const onDoc = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportOpen(false);
        setExportDone(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const clearFilters = () => {
    setDates({ start: null, end: null });
    setWeek("");
    setSite("");
    setAaAlias("");
    setSessionsPage(1);
  };

  const hasActiveFilters = Boolean(week || site || aaAlias || dates.start || dates.end);

  const downloadSessionsCsv = async () => {
    if (!groupId || !configData?.config?.API_URL || !userData?.token || exporting) return;
    setExporting(true);
    setExportDone(false);
    try {
      const response = await axios.get(
        `${configData.config.API_URL}${FREEZER_FROST_DASHBOARD_SESSIONS_EXPORT}/${groupId}/export`,
        {
          headers: { Authorization: `Bearer ${userData.token}` },
          params: buildFilterParams(),
          responseType: "blob",
        }
      );
      const stamp = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(response.data);
      link.download = `freezer-frost-dashboard-report-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      setExportDone(true);
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  };

  const openNonComplianceView = async () => {
    setShowNcModal(true);
    setNcLoading(true);
    setExpandedNc(null);
    try {
      const data = await apiClient.get(
        `${configData.config.API_URL}${FREEZER_FROST_NON_COMPLIANCE_LIST}/${groupId}`,
        { params: buildFilterParams() }
      );
      setNcList(Array.isArray(data) ? data : []);
    } catch (error) {
      setNcList([]);
    } finally {
      setNcLoading(false);
    }
  };

  const openCumulativeView = async () => {
    setShowCumulativeModal(true);
    setCumulativeLoading(true);
    try {
      const data = await apiClient.get(
        `${configData.config.API_URL}${FREEZER_FROST_CUMULATIVE_LIMIT_LIST}/${groupId}`,
        { params: buildFilterParams() }
      );
      setCumulativeList(Array.isArray(data) ? data : []);
    } catch (error) {
      setCumulativeList([]);
    } finally {
      setCumulativeLoading(false);
    }
  };

  const openExceededView = async () => {
    setShowExceededModal(true);
    setExceededLoading(true);
    try {
      const data = await apiClient.get(
        `${configData.config.API_URL}${FREEZER_FROST_EXCEEDED_EXPOSURE_LIST}/${groupId}`,
        { params: buildFilterParams() }
      );
      setExceededList(Array.isArray(data) ? data : []);
    } catch (error) {
      setExceededList([]);
    } finally {
      setExceededLoading(false);
    }
  };

  const openActiveInsideView = async () => {
    setShowActiveModal(true);
    setActiveLoading(true);
    try {
      const data = await apiClient.get(
        `${configData.config.API_URL}${FREEZER_FROST_ACTIVE_INSIDE_LIST}/${groupId}`,
        { params: buildFilterParams() }
      );
      setActiveList(Array.isArray(data) ? data : []);
    } catch (error) {
      setActiveList([]);
    } finally {
      setActiveLoading(false);
    }
  };

  const cards = [
    {
      key: "visits",
      label: "Total Freezer Visits",
      value: stats?.totalVisits ?? stats?.totalUnits,
      icon: <AcUnitOutlinedIcon />,
      tone: "purple",
    },
    {
      key: "active",
      label: "AAs Active Inside Freezer",
      value: stats?.activeSessions,
      icon: <SensorsOutlinedIcon />,
      tone: "live",
      live: true,
      viewAction: "active",
    },
    {
      key: "completed",
      label: "AAs Completed within exposure limit",
      value: stats?.completedSessions ?? stats?.completedToday,
      icon: <CheckCircleOutlineIcon />,
      tone: "green",
    },
    {
      key: "missed",
      label: "AAs Exceeded Exposure for single entry",
      value: stats?.missedExit,
      icon: <TimerOffOutlinedIcon />,
      tone: "red",
      alertOnCount: true,
      viewAction: "exceeded",
    },
    {
      key: "cumulative",
      label: "Cumulative Limit Reached for single shift",
      value: stats?.cumulativeLimitReached,
      icon: <HourglassTopOutlinedIcon />,
      tone: "red",
      alertOnCount: true,
      viewAction: "cumulative",
    },
    {
      key: "avg",
      label: "Average Operation Time",
      value: stats?.avgDurationMinutes != null ? `${stats.avgDurationMinutes} min` : "0 min",
      icon: <TimelapseOutlinedIcon />,
      tone: "purple",
    },
    {
      key: "nc",
      label: "Non-Compliances Reported During Freezer Entry",
      value: stats?.nonComplianceSessions,
      icon: <GppBadOutlinedIcon />,
      tone: "red",
      viewAction: "nc",
    },
  ];

  const iconTone = (tone) => {
    if (tone === "live") return "live";
    if (tone === "orange" || tone === "amber") return "amber";
    if (tone === "red") return "red";
    return tone;
  };

  return (
    <div className="dashboard-v2 site-dashboard-v2 ff-dashboard-v2">
      <div className="dashboard-v2__top ff-dashboard-v2__top">
        <div className="machines-v2__tabs dashboard-v2__tabs site-dashboard-v2__tabs">
          <button type="button" className="machines-v2__tab active">
            Freezer-Frost Dashboard
          </button>
        </div>
        <div className="ff-dashboard-v2__top-actions">
          <div className="ff-dash-export" ref={exportRef}>
            <button
              type="button"
              className={`ff-dashboard-v2__share-btn ff-dash-export__trigger${exportOpen ? " is-open" : ""}`}
              onClick={() => {
                setExportOpen((v) => !v);
                setExportDone(false);
              }}
              aria-expanded={exportOpen}
              aria-haspopup="dialog"
            >
              <FileDownloadOutlinedIcon />
              Export report
              <KeyboardArrowDownIcon className="ff-dash-export__chevron" fontSize="small" />
            </button>

            {exportOpen ? (
              <div className="ff-dash-export__panel" role="dialog" aria-label="Download dashboard report">
                <div className="ff-dash-export__panel-head">
                  <div>
                    <strong>Download dashboard report</strong>
                    <p>Recent Freezer Sessions — full field export</p>
                  </div>
                  <button
                    type="button"
                    className="ff-dash-export__panel-close"
                    onClick={() => setExportOpen(false)}
                    aria-label="Close"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>

                <ul className="ff-dash-export__points">
                  <li>
                    <span className="ff-dash-export__point-icon" aria-hidden>
                      <DescriptionOutlinedIcon />
                    </span>
                    <div>
                      <strong>Freezer-Frost dashboard report</strong>
                      <span>All sessions matching your current filters (date / week / site / AA alias).</span>
                    </div>
                  </li>
                  <li>
                    <span className="ff-dash-export__point-icon is-csv" aria-hidden>
                      <TableChartOutlinedIcon />
                    </span>
                    <div>
                      <strong>CSV file (.csv)</strong>
                      <span>Opens directly in Excel, Google Sheets, or Numbers.</span>
                    </div>
                  </li>
                </ul>

                <div className="ff-dash-export__meta">
                  <span className="ff-dash-export__badge">CSV</span>
                  <span>
                    {hasActiveFilters
                      ? "Uses active filters on this dashboard"
                      : "All sessions (no filters applied)"}
                  </span>
                </div>

                {exportDone ? (
                  <p className="ff-dash-export__done">Report downloaded — check your Downloads folder.</p>
                ) : null}

                <div className="ff-dash-export__foot">
                  <button type="button" className="ff-dash-export__cancel" onClick={() => setExportOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ff-dash-export__primary"
                    onClick={downloadSessionsCsv}
                    disabled={exporting || loading}
                  >
                    <FileDownloadOutlinedIcon fontSize="small" />
                    {exporting ? "Preparing CSV…" : "Download CSV report"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <button type="button" className="ff-dashboard-v2__share-btn" onClick={() => setShowShare(true)}>
            <ShareOutlinedIcon />
            Share
          </button>
          <FreezerFrostFilters
            dates={dates}
            setDates={setDates}
            week={week}
            setWeek={setWeek}
            site={site}
            setSite={setSite}
            aaAlias={aaAlias}
            setAaAlias={setAaAlias}
            filterOptions={filterOptions}
            onClear={clearFilters}
          />
        </div>
      </div>

      <div className="dashboard-v2__stats site-dashboard-v2__stats ff-dashboard-v2__stats">
        {cards.map((card) => {
          const numericValue = Number(card.value);
          const isAlert = !!card.alertOnCount && Number.isFinite(numericValue) && numericValue >= 1;
          const showPulse = !!card.live || isAlert;
          return (
          <article
            className={`sites-v2__stat-card dashboard-v2__mini-stat ff-dashboard-v2__stat-card${card.live ? " ff-dashboard-v2__stat-card--live" : ""}${isAlert ? " ff-dashboard-v2__stat-card--alert-red" : ""}`}
            key={card.key}
          >
            <div className={`sites-v2__stat-icon sites-v2__stat-icon--${iconTone(card.tone)} ff-dashboard-v2__stat-icon${card.live ? " ff-dashboard-v2__stat-icon--live-pulse" : ""}${isAlert ? " ff-dashboard-v2__stat-icon--alert-pulse" : ""}`}>
              {showPulse ? (
                <span className={`ff-dashboard-v2__live-wrap${isAlert ? " is-alert" : ""}`} aria-hidden>
                  <span className="ff-dashboard-v2__live-ping" />
                  <span className="ff-dashboard-v2__live-ping ff-dashboard-v2__live-ping--delay" />
                  <span className="ff-dashboard-v2__live-dot" />
                  {card.icon}
                </span>
              ) : (
                card.icon
              )}
            </div>
            <div className="ff-dashboard-v2__stat-body">
              <div className="ff-dashboard-v2__stat-value-row">
                <strong>{loading ? <Skeleton width={40} /> : card.value ?? 0}</strong>
                {card.live ? (
                  <span className="ff-dashboard-v2__live-badge">
                    <span className="ff-dashboard-v2__live-badge-dot" />
                    LIVE
                  </span>
                ) : null}
                {isAlert ? (
                  <span className="ff-dashboard-v2__alert-badge">
                    <span className="ff-dashboard-v2__alert-badge-dot" />
                    ALERT
                  </span>
                ) : null}
              </div>
              <span>{card.label}</span>
              {card.viewAction === "nc" ? (
                <button type="button" className="ff-dashboard-v2__view-btn" onClick={openNonComplianceView} disabled={loading}>
                  View
                </button>
              ) : null}
              {card.viewAction === "exceeded" ? (
                <button type="button" className="ff-dashboard-v2__view-btn" onClick={openExceededView} disabled={loading}>
                  View
                </button>
              ) : null}
              {card.viewAction === "cumulative" ? (
                <button type="button" className="ff-dashboard-v2__view-btn" onClick={openCumulativeView} disabled={loading}>
                  View
                </button>
              ) : null}
              {card.viewAction === "active" ? (
                <button type="button" className="ff-dashboard-v2__view-btn" onClick={openActiveInsideView} disabled={loading}>
                  View
                </button>
              ) : null}
            </div>
          </article>
          );
        })}
      </div>

      <section className="dashboard-v2__table-section ff-dashboard-v2__sessions">
        <div className="ff-dashboard-v2__sessions-head">
          <div>
            <h2 className="dashboard-v2__table-title">
              Recent <span>Freezer Sessions</span>
            </h2>
            <p className="ff-dashboard-v2__sessions-sub">
              Latest entry / exit activity across Freezer-Frost units · auto-updates every{" "}
              {DASHBOARD_REFRESH_MS / 1000}s
            </p>
          </div>
          <div className="ff-dashboard-v2__sessions-head-meta">
            {lastRefreshedAt ? (
              <span className="ff-dashboard-v2__sessions-refresh">
                Last refresh: {lastRefreshedAt.toLocaleTimeString()}
              </span>
            ) : null}
            {!loading && (stats?.recentSessionsTotal || stats?.recentSessions?.length) ? (
              <span className="ff-dashboard-v2__sessions-count">
                {stats.recentSessionsTotal || stats.recentSessions.length} total · page{" "}
                {stats.recentSessionsPage || sessionsPage}
              </span>
            ) : null}
          </div>
        </div>

        <div className="dashboard-v2__table-wrap ff-dashboard-v2__sessions-card">
          <div className="table-responsive">
            <table className="table dashboard-v2__table ff-dashboard-v2__sessions-table mb-0">
              <thead>
                <tr>
                  <th>AA Alias</th>
                  <th>Freezer</th>
                  <th>Entry Time</th>
                  <th>Exit Time</th>
                  <th>Duration</th>
                  <th>Remaining</th>
                  <th />
                  <th>Session Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>
                      <Skeleton count={4} />
                    </td>
                  </tr>
                ) : stats?.recentSessions?.length ? (
                  stats.recentSessions.map((row) => {
                    const live = isLiveInsideSession(row);
                    const remaining = live
                      ? row.sessionStatus === "cumulative_limit_reached"
                        ? { text: "Cumulative limit reached.", tone: "danger", reached: true }
                        : formatRemainingExposure(row.entryTime, row.exposureLimitSeconds, nowMs)
                      : { text: "—", tone: "muted", reached: false };
                    const durationAlert = live && (remaining.tone === "danger" || remaining.reached);
                    return (
                    <tr key={row.uuid}>
                      <td>
                        <span className="ff-dashboard-v2__aa-alias">{row.aaAlias || "—"}</span>
                      </td>
                      <td>
                        <div className="ff-dashboard-v2__sessions-freezer">
                          <span className="ff-dashboard-v2__sessions-freezer-icon" aria-hidden>
                            <AcUnitOutlinedIcon fontSize="inherit" />
                          </span>
                          <strong>{row.freezerName || "—"}</strong>
                        </div>
                      </td>
                      <td>
                        <span className="ff-dashboard-v2__sessions-time">{formatWhen(row.entryTime)}</span>
                      </td>
                      <td>
                        <span className="ff-dashboard-v2__sessions-time">{displayExitTime(row)}</span>
                      </td>
                      <td>
                        {live ? (
                          <span
                            className={`ff-dashboard-v2__sessions-duration is-live${durationAlert ? " is-alert" : ""}`}
                            title="Live exposure duration"
                          >
                            <span className="ff-dashboard-v2__duration-live-dot" aria-hidden />
                            {formatLiveElapsed(row.entryTime, nowMs)}
                          </span>
                        ) : (
                          <span className="ff-dashboard-v2__sessions-duration">{formatDuration(row.durationSeconds)}</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`ff-dashboard-v2__sessions-remaining is-${remaining.tone}${remaining.reached ? " is-reached" : ""}`}
                          title={live ? "Remaining exposure time" : undefined}
                        >
                          {remaining.text}
                        </span>
                      </td>
                      <td className="ff-dashboard-v2__sessions-action">
                        <Link className="dashboard-v2__link ff-dashboard-v2__sessions-link" to={`/ff/session/${row.uuid}`}>
                          View
                        </Link>
                      </td>
                      <td>
                        <SessionStatusIndicator
                          sessionStatus={row.sessionStatus || row.status}
                          isOpenSession={!!row.isOpenSession}
                        />
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <div className="ff-dashboard-v2__sessions-empty">
                        <AcUnitOutlinedIcon />
                        <strong>No freezer sessions yet</strong>
                        <span>Sessions will appear here after entry / exit scans.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && (stats?.recentSessionsTotal || 0) > 0 ? (
            <div className="ff-sessions-pagination">
              <p>
                Showing{" "}
                {(stats.recentSessionsTotal || 0) === 0
                  ? 0
                  : ((stats.recentSessionsPage || sessionsPage) - 1) * RECENT_SESSIONS_PAGE_SIZE + 1}{" "}
                to{" "}
                {Math.min(
                  (stats.recentSessionsPage || sessionsPage) * RECENT_SESSIONS_PAGE_SIZE,
                  stats.recentSessionsTotal || 0
                )}{" "}
                of {stats.recentSessionsTotal || 0} sessions
              </p>
              <div className="ff-sessions-pagination__controls">
                <button
                  type="button"
                  disabled={(stats.recentSessionsPage || sessionsPage) <= 1}
                  onClick={() => setSessionsPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <span className="active">{stats.recentSessionsPage || sessionsPage}</span>
                <button
                  type="button"
                  disabled={
                    (stats.recentSessionsPage || sessionsPage) >= (stats.recentSessionsTotalPages || 1)
                  }
                  onClick={() =>
                    setSessionsPage((p) =>
                      Math.min(stats.recentSessionsTotalPages || p + 1, p + 1)
                    )
                  }
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {showNcModal ? (
        <div className="ff-dashboard-v2__modal-overlay" onClick={() => setShowNcModal(false)}>
          <div
            className="ff-dashboard-v2__modal"
            role="dialog"
            aria-modal="true"
            aria-label="Non-compliance sessions"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ff-dashboard-v2__modal-head">
              <div>
                <h3>Non-Compliances — Freezer Entry</h3>
                <p>Each row is one closed session. Expand to see No answers in that session.</p>
              </div>
              <button type="button" className="ff-dashboard-v2__modal-close" onClick={() => setShowNcModal(false)} aria-label="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="ff-dashboard-v2__modal-body">
              {ncLoading ? (
                <Skeleton count={4} />
              ) : ncList.length === 0 ? (
                <p className="ff-dashboard-v2__empty">No non-compliance sessions in this range.</p>
              ) : (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>No Count</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {ncList.map((row) => (
                      <React.Fragment key={row.uuid}>
                        <tr>
                          <td>{row.freezerName || "—"}</td>
                          <td>{formatWhen(row.entryTime)}</td>
                          <td>{row.nonComplianceCount ?? 0}</td>
                          <td>Session closed</td>
                          <td className="ff-dashboard-v2__nc-actions">
                            <button
                              type="button"
                              className="ff-dashboard-v2__view-btn"
                              onClick={() => setExpandedNc(expandedNc === row.uuid ? null : row.uuid)}
                            >
                              {expandedNc === row.uuid ? "Hide Nos" : "Nos"}
                            </button>
                            <Link className="ff-dashboard-v2__view-btn" to={`/ff/session/${row.uuid}`}>
                              Details
                            </Link>
                          </td>
                        </tr>
                        {expandedNc === row.uuid ? (
                          <tr className="ff-dashboard-v2__nc-detail-row">
                            <td colSpan={5}>
                              {row.noAnswers?.length ? (
                                <ul className="ff-dashboard-v2__no-list">
                                  {row.noAnswers.map((item, index) => (
                                    <li key={item.questionId || index}>
                                      <strong>No</strong> — {item.question || item.title || "Question"}
                                      {item.attachments?.length ? (
                                        <span className="ff-dashboard-v2__no-meta"> · {item.attachments.length} evidence</span>
                                      ) : null}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="ff-dashboard-v2__empty">
                                  {row.nonComplianceCount || 0} No response(s) recorded in this session.
                                </p>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showCumulativeModal ? (
        <div className="ff-dashboard-v2__modal-overlay" onClick={() => setShowCumulativeModal(false)}>
          <div
            className="ff-dashboard-v2__modal"
            role="dialog"
            aria-modal="true"
            aria-label="Cumulative limit reached sessions"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ff-dashboard-v2__modal-head">
              <div>
                <h3>Cumulative Limit Reached for single shift</h3>
                <p>Sessions where this AA Alias hit the rolling cumulative exposure limit.</p>
              </div>
              <button
                type="button"
                className="ff-dashboard-v2__modal-close"
                onClick={() => setShowCumulativeModal(false)}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="ff-dashboard-v2__modal-body">
              {cumulativeLoading ? (
                <Skeleton count={4} />
              ) : cumulativeList.length === 0 ? (
                <p className="ff-dashboard-v2__empty">No cumulative-limit sessions in this range.</p>
              ) : (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>AA Alias</th>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cumulativeList.map((row) => (
                      <tr key={row.uuid}>
                        <td>{row.aaAlias || "—"}</td>
                        <td>{row.freezerName || "—"}</td>
                        <td>{formatWhen(row.entryTime)}</td>
                        <td>{row.exitProcessComplete ? formatWhen(row.exitTime) : "—"}</td>
                        <td>
                          {row.isOpenSession
                            ? formatLiveElapsed(row.entryTime, nowMs)
                            : formatDuration(row.durationSeconds)}
                        </td>
                        <td>
                          <SessionStatusIndicator
                            sessionStatus={row.sessionStatus || "cumulative_limit_reached"}
                            isOpenSession={!!row.isOpenSession}
                          />
                        </td>
                        <td>
                          <Link className="ff-dashboard-v2__view-btn" to={`/ff/session/${row.uuid}`}>
                            Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showExceededModal ? (
        <div className="ff-dashboard-v2__modal-overlay" onClick={() => setShowExceededModal(false)}>
          <div
            className="ff-dashboard-v2__modal"
            role="dialog"
            aria-modal="true"
            aria-label="Exceeded exposure for single entry"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ff-dashboard-v2__modal-head">
              <div>
                <h3>AAs Exceeded Exposure for single entry</h3>
                <p>Still inside — per-visit exposure / exit timeout exceeded.</p>
              </div>
              <button
                type="button"
                className="ff-dashboard-v2__modal-close"
                onClick={() => setShowExceededModal(false)}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="ff-dashboard-v2__modal-body">
              {exceededLoading ? (
                <Skeleton count={4} />
              ) : exceededList.length === 0 ? (
                <p className="ff-dashboard-v2__empty">No exceeded-exposure sessions in this range.</p>
              ) : (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>AA Alias</th>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>Duration</th>
                      <th>Remaining</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {exceededList.map((row) => {
                      const remaining = { text: "Exposure limit reached.", tone: "danger", reached: true };
                      return (
                        <tr key={row.uuid}>
                          <td>
                            <span className="ff-dashboard-v2__aa-alias">{row.aaAlias || "—"}</span>
                          </td>
                          <td>{row.freezerName || "—"}</td>
                          <td>{formatWhen(row.entryTime)}</td>
                          <td>
                            <span className="ff-dashboard-v2__sessions-duration is-live is-alert">
                              <span className="ff-dashboard-v2__duration-live-dot" aria-hidden />
                              {formatLiveElapsed(row.entryTime, nowMs)}
                            </span>
                          </td>
                          <td>
                            <span className="ff-dashboard-v2__sessions-remaining is-danger is-reached">
                              {remaining.text}
                            </span>
                          </td>
                          <td>
                            <SessionStatusIndicator
                              sessionStatus={row.sessionStatus || "exceeded_no_exit"}
                              isOpenSession={!!row.isOpenSession}
                            />
                          </td>
                          <td>
                            <Link className="ff-dashboard-v2__view-btn" to={`/ff/session/${row.uuid}`}>
                              Details
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showActiveModal ? (
        <div className="ff-dashboard-v2__modal-overlay" onClick={() => setShowActiveModal(false)}>
          <div
            className="ff-dashboard-v2__modal"
            role="dialog"
            aria-modal="true"
            aria-label="AAs active inside freezer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ff-dashboard-v2__modal-head">
              <div>
                <h3>AAs Active Inside Freezer</h3>
                <p>Still inside — Exit QR + questionnaire not completed yet.</p>
              </div>
              <button
                type="button"
                className="ff-dashboard-v2__modal-close"
                onClick={() => setShowActiveModal(false)}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="ff-dashboard-v2__modal-body">
              {activeLoading ? (
                <Skeleton count={4} />
              ) : activeList.length === 0 ? (
                <p className="ff-dashboard-v2__empty">No one is inside a freezer right now.</p>
              ) : (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>AA Alias</th>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>Duration</th>
                      <th>Remaining</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {activeList.map((row) => {
                      const remaining =
                        row.sessionStatus === "cumulative_limit_reached"
                          ? { text: "Cumulative limit reached.", tone: "danger", reached: true }
                          : formatRemainingExposure(row.entryTime, row.exposureLimitSeconds, nowMs);
                      const durationAlert = remaining.tone === "danger" || remaining.reached;
                      return (
                        <tr key={row.uuid}>
                          <td>
                            <span className="ff-dashboard-v2__aa-alias">{row.aaAlias || "—"}</span>
                          </td>
                          <td>{row.freezerName || "—"}</td>
                          <td>{formatWhen(row.entryTime)}</td>
                          <td>
                            <span
                              className={`ff-dashboard-v2__sessions-duration is-live${durationAlert ? " is-alert" : ""}`}
                            >
                              <span className="ff-dashboard-v2__duration-live-dot" aria-hidden />
                              {formatLiveElapsed(row.entryTime, nowMs)}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`ff-dashboard-v2__sessions-remaining is-${remaining.tone}${
                                remaining.reached ? " is-reached" : ""
                              }`}
                            >
                              {remaining.text}
                            </span>
                          </td>
                          <td>
                            <SessionStatusIndicator
                              sessionStatus={row.sessionStatus || "in_progress"}
                              isOpenSession
                            />
                          </td>
                          <td>
                            <Link className="ff-dashboard-v2__view-btn" to={`/ff/session/${row.uuid}`}>
                              Details
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <FreezerFrostShareModal
        open={showShare}
        onClose={() => setShowShare(false)}
        groupId={groupId}
        groupName={groupName}
        apiUrl={configData?.config?.API_URL}
        token={userData?.token}
      />
    </div>
  );
};

export default FreezerFrostDashboard;
